import * as THREE from "three";
import type { FloorplannerStore } from '../../store/floorplannerStore';
import type { EditorStoreType } from '../../store/editorStore';
import {
  isWallType,
  isSingleLineType,
  RulerLineType,
  SingleLineType,
  SymbolType,
  WallConnectionEnd,
  WallConnectionStart,
  WallType,
  WallEdgeConnection,
  isEdgeConnection,
} from "../../types/floorplannerTypes";
import { renderStore } from "../../store/renderStore";
import { getSymbolInsertGeometry, getSymbolRenderCenterOffset } from "../../utils/symbolAttachmentGeometry";
import { jointCoordinator } from '../../utils/JointCoordinator';
import { sharedJointStateManager } from '../../utils/SharedJointStateManager';
import { createEdgeConnection, getWallAngle, recomputeEdgeConnectionForCurrentGeometry } from "../../utils/edgeConnectionHelpers";
import { getConnectionPoint } from "../../utils/connectionGeometry";
import { runInAction } from "mobx";

// Cache the wallUpdateManager import at module level to avoid repeated dynamic imports
import { wallUpdateManager } from "../../utils/wallUpdateManager";

// Lazy accessors to avoid circular dependency during store initialization
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - updateWallConnections called too early');
    }
  }
  return _floorplannerStore;
};

let _editorStore: EditorStoreType | null = null;
const getEditorStore = (): EditorStoreType | null => {
  if (!_editorStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniEditorStore?: EditorStoreType }).__boniEditorStore;
    if (globalStore) {
      _editorStore = globalStore;
    }
  }
  return _editorStore;
};

type ConnectableLineObject = WallType | SingleLineType | RulerLineType;

const isConnectableLineObject = (object: unknown): object is ConnectableLineObject => {
  return Boolean(
    object &&
    typeof object === "object" &&
    "type" in object &&
    ((object as { type?: string }).type === "wall" ||
      (object as { type?: string }).type === "singleLine" ||
      (object as { type?: string }).type === "rulerLine") &&
    "start" in object &&
    "end" in object
  );
};

const PERPENDICULAR_DOT_THRESHOLD = 0.2;
const EXACT_PERPENDICULAR_DOT_THRESHOLD = 0.01;
const PARALLEL_DOT_THRESHOLD = 0.999;

const getObjectDirection = (object: WallType | SingleLineType): THREE.Vector2 | null => {
  const direction = new THREE.Vector2().subVectors(object.end, object.start);
  if (direction.lengthSq() === 0) {
    return null;
  }
  return direction.normalize();
};

const getConnectedMiddleHandleObject = (objectId: string): WallType | SingleLineType | null => {
  const connectedObject = getFloorplannerStore().findObjectId(objectId);
  if (connectedObject && (isWallType(connectedObject) || isSingleLineType(connectedObject))) {
    return connectedObject;
  }
  return null;
};

const isEndpointPosition = (position: number): boolean =>
  position === WallConnectionStart || position === WallConnectionEnd;

const canSlideEndpointAlongMiddleConnection = (
  objectDirection: THREE.Vector2,
  targetObject: WallType | SingleLineType,
  moveDirection: THREE.Vector2,
): boolean => {
  const targetDirection = getObjectDirection(targetObject);
  if (!targetDirection) {
    return false;
  }

  const objectIsPerpendicularToTarget = Math.abs(objectDirection.dot(targetDirection)) <= EXACT_PERPENDICULAR_DOT_THRESHOLD;
  const moveStaysOnTargetLine = Math.abs(moveDirection.dot(targetDirection)) >= PARALLEL_DOT_THRESHOLD;

  return objectIsPerpendicularToTarget && moveStaysOnTargetLine;
};

const hasBlockingEndpointToMiddleConnection = (
  object: WallType | SingleLineType,
  endpoint: "start" | "end",
  objectDirection: THREE.Vector2,
  moveDirection: THREE.Vector2,
): boolean => {
  const endpointPosition = endpoint === "start" ? WallConnectionStart : WallConnectionEnd;

  const centerConnections = object.centerConnections?.filter(connection =>
    connection.sourcePosition === endpointPosition && !isEndpointPosition(connection.targetPosition)
  ) || [];

  for (const connection of centerConnections) {
    const connectedObject = getConnectedMiddleHandleObject(connection.id);
    if (!connectedObject) {
      continue;
    }
    if (!canSlideEndpointAlongMiddleConnection(objectDirection, connectedObject, moveDirection)) {
      return true;
    }
  }

  if (isWallType(object)) {
    const edgeMiddleConnections = object.edgeConnections?.filter(connection =>
      connection.sourceEndpoint === endpoint && connection.targetEndpoint === "middle"
    ) || [];

    for (const connection of edgeMiddleConnections) {
      const connectedObject = getConnectedMiddleHandleObject(connection.targetWallId);
      if (!connectedObject) {
        continue;
      }
      if (!canSlideEndpointAlongMiddleConnection(objectDirection, connectedObject, moveDirection)) {
        return true;
      }
    }
  }

  return false;
};

const getEndpointConnectedObjects = (object: WallType | SingleLineType, endpoint: "start" | "end"): (WallType | SingleLineType)[] => {
  const connectedObjects = new Map<string, WallType | SingleLineType>();
  const addObject = (objectId: string) => {
    const connectedObject = getConnectedMiddleHandleObject(objectId);
    if (connectedObject) {
      connectedObjects.set(connectedObject.id, connectedObject);
    }
  };

  if (isWallType(object)) {
    const edgeConnections = object.edgeConnections?.filter(conn => conn.sourceEndpoint === endpoint) || [];
    edgeConnections.forEach(conn => addObject(conn.targetWallId));
  }

  const endpointPosition = endpoint === "start" ? WallConnectionStart : WallConnectionEnd;
  const centerConnections = object.centerConnections?.filter(conn => conn.sourcePosition === endpointPosition) || [];
  centerConnections.forEach(conn => addObject(conn.id));

  return Array.from(connectedObjects.values());
};

/**
 * Block middle-handle movement that would leave persistent endpoint connections
 * geometrically invalid. Endpoint-to-middle connections are locked unless the
 * move is the perpendicular T-junction slide case, where the endpoint remains
 * on the target line and only the target attachment distance changes.
 */
export const isMiddleHandleMovementBlocked = (object: WallType | SingleLineType, moveDirection: THREE.Vector2): boolean => {
  const moveDir = moveDirection.clone();
  if (moveDir.lengthSq() === 0) {
    return false;
  }
  moveDir.normalize();

  const objectDirection = getObjectDirection(object);
  if (!objectDirection) {
    return false;
  }

  if (
    hasBlockingEndpointToMiddleConnection(object, "start", objectDirection, moveDir) ||
    hasBlockingEndpointToMiddleConnection(object, "end", objectDirection, moveDir)
  ) {
    return true;
  }

  const startConnectedObjects = getEndpointConnectedObjects(object, "start");
  const endConnectedObjects = getEndpointConnectedObjects(object, "end");

  if (startConnectedObjects.length === 0 || endConnectedObjects.length === 0) {
    return false;
  }

  const isEndpointBlocked = (connectedObjects: (WallType | SingleLineType)[]) =>
    connectedObjects.some(connectedObject => {
      const connectedDir = getObjectDirection(connectedObject);
      return connectedDir ? Math.abs(connectedDir.dot(moveDir)) < PERPENDICULAR_DOT_THRESHOLD : false;
    });

  return isEndpointBlocked(startConnectedObjects) && isEndpointBlocked(endConnectedObjects);
};

const pointsDiffer = (a: THREE.Vector2, b: THREE.Vector2): boolean => {
  return a.distanceToSquared(b) > 1e-12;
};

const updateConnectedEndpoint = (
  object: ConnectableLineObject,
  targetPosition: number,
  newPosition: THREE.Vector2,
): boolean => {
  if (targetPosition === WallConnectionStart && pointsDiffer(object.start, newPosition)) {
    object.start = newPosition.clone();
    return true;
  }

  if (targetPosition === WallConnectionEnd && pointsDiffer(object.end, newPosition)) {
    object.end = newPosition.clone();
    return true;
  }

  return false;
};

const updateCenterConnectionsForSource = (
  sourceObject: ConnectableLineObject,
  dragHandle: string | null,
  activeStore: FloorplannerStore,
  blockedTargetIds: Set<string>,
): string[] => {
  const changedObjectIds: string[] = [];

  sourceObject.centerConnections?.forEach((connection) => {
    if (connection.mode === "edge") {
      return;
    }

    // Endpoint connections are handled by the edge connection path and the
    // drag-handle code. Keep this helper focused on center/middle attachments.
    if (connection.sourcePosition === WallConnectionStart || connection.sourcePosition === WallConnectionEnd) {
      return;
    }

    // dragHandle === null means this is not a real drag operation (e.g. wall selection, paste).
    // dragHandle === "wall" means whole-wall drag which shouldn't update mid-point connections.
    if (dragHandle === null || dragHandle === "wall") {
      return;
    }

    const object = activeStore.findObjectId(connection.id);
    if (!isConnectableLineObject(object) || blockedTargetIds.has(object.id)) {
      return;
    }

    const newPosition = getConnectionPoint(
      sourceObject.start,
      sourceObject.end,
      connection.sourcePosition,
    );

    if (updateConnectedEndpoint(object, connection.targetPosition, newPosition)) {
      changedObjectIds.push(object.id);
    }
  });

  return changedObjectIds;
};

export const cascadeCenterConnectionUpdates = (
  sourceIds: string[],
  store?: FloorplannerStore,
  blockedSourceIds: Set<string> = new Set(),
): string[] => {
  const activeStore = store || getFloorplannerStore();
  const changedObjectIds = new Set<string>();
  const queue = [...sourceIds];
  const visitedSources = new Set(blockedSourceIds);

  while (queue.length > 0) {
    const sourceId = queue.shift()!;
    if (visitedSources.has(sourceId)) {
      continue;
    }
    visitedSources.add(sourceId);

    const sourceObject = activeStore.findObjectId(sourceId);
    if (!isConnectableLineObject(sourceObject)) {
      continue;
    }

    const changedIds = updateCenterConnectionsForSource(
      sourceObject,
      "cascade",
      activeStore,
      visitedSources,
    );

    changedIds.forEach((changedId) => {
      changedObjectIds.add(changedId);
      if (!visitedSources.has(changedId)) {
        queue.push(changedId);
      }
    });
  }

  return Array.from(changedObjectIds);
};

export const updateWallClippings = (wallId: string, symbolId?: string, store?: FloorplannerStore) => {
  const activeStore = store || getFloorplannerStore();
  const wall = activeStore?.wallsMap.get(wallId);
  if (!wall) return;

  renderStore.clearSymbolClippings(wall.id);
  const symbols = [] as SymbolType[];
  if (symbolId) {
    const symbol = activeStore?.symbolsMap.get(symbolId);
    if (symbol) {
      symbols.push(symbol);
    }
  } else {
    wall.symbolAttachments?.forEach((symbolAttachment) => {
      const symbol = activeStore?.symbolsMap.get(symbolAttachment.symbolId);
      if (symbol) {
        symbols.push(symbol);
      }
    });
  }
  // Add the new symbol clippings
  symbols.forEach((symbol) => {
    const clippingPolygon = renderStore.getClippingPolygon(
      symbol,
      wall
    );
    if (clippingPolygon) {
      renderStore.addWallClipping(wall.id, clippingPolygon);
    }
  })
}

/**
 * Updates symbol attachments for a wall during drag operations without triggering full connection updates.
 * This function is optimized for real-time updates during wall dragging.
 * @param wallId The ID of the wall whose symbol attachments should be updated
 * @param store Optional store instance (defaults to global floorplannerStore)
 */
export const updateSymbolAttachmentsDuringDrag = (wallId: string, store?: FloorplannerStore) => {
  const activeStore = store || getFloorplannerStore();
  const wall = activeStore?.wallsMap.get(wallId) as WallType;

  if (!wall || !isWallType(wall) || !wall.symbolAttachments) {
    return;
  }

  let shouldRefreshWallClippings = false;

  wall.symbolAttachments.forEach((symbolAttachment) => {
    // Update symbol attachment positions
    const symbol = activeStore?.symbolsMap.get(symbolAttachment.symbolId);
    if (!symbol) return;
    const tempWall = activeStore?.wallsMap.get(wall.id);
    if (!tempWall) return;

    try {
      // Recompute position and rotation using the shared insert-geometry helper.
      // For arc walls this uses the straight-insert chord (midpoint + chord direction).
      // For straight walls this is equivalent to the previous chord-projection logic.
      const defaultFrameWidth = activeStore?.doorFrameWidth ?? 0.05;
      const insertGeom = getSymbolInsertGeometry(tempWall, symbol, symbolAttachment.positionFromStart, defaultFrameWidth);
      const renderCenterOffset = getSymbolRenderCenterOffset(
        tempWall,
        symbol,
        symbolAttachment.positionFromStart,
        defaultFrameWidth,
      );
      const newPosition = insertGeom.midpoint.clone().sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
      const newWallAngle = Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x);

      // Only update if the position or rotation actually changed
      const positionChanged = newPosition.x !== symbol.position.x || newPosition.y !== symbol.position.y;
      const rotationChanged = (symbol.rotation ?? 0) !== newWallAngle;

      if (positionChanged || rotationChanged) {
        activeStore?.updateSymbolProperty(
          symbolAttachment.symbolId,
          "position",
          newPosition.toArray(),
        );
        activeStore?.updateSymbolProperty(
          symbolAttachment.symbolId,
          "rotation",
          newWallAngle,
        );
      }

      if (!renderStore.isDragging) {
        shouldRefreshWallClippings = true;
      }
    } finally {
      // nothing to release — no pooled vectors used above
    }
  });

  if (shouldRefreshWallClippings) {
    updateWallClippings(wall.id, undefined, activeStore);
  }
}

export const updateWallConnections = (
  source: string,
  dragHandle: string | null,
  updatedWalls?: string[],
  store?: FloorplannerStore
) => {
  // Use provided store or fallback to global store
  const activeStore = store || getFloorplannerStore();

  const wall = activeStore?.findObjectId(source) as WallType | SingleLineType | RulerLineType;
  let updated = false;
  const newUpdatedWalls = updatedWalls || [];

  // Safely bail if the wall is missing from the store
  if (!wall) return;

  // Update edge connections (skip during drag for performance)
  if (isWallType(wall) &&
    !(activeStore?.isDragInProgress && activeStore.isDragInProgress() && dragHandle !== null)) {
    // Iterate through edge connections
    wall.edgeConnections?.forEach((edgeConnection) => {
      // Determine which endpoint was dragged
      const sourceEndpoint = edgeConnection.sourceEndpoint;

      // Check if this edge connection is at the dragged endpoint
      // Treat dragHandle === null as a full-wall move and update all edge connections
      const shouldUpdate = dragHandle === null ||
        (dragHandle === "start" && sourceEndpoint === "start") ||
        (dragHandle === "end" && sourceEndpoint === "end") ||
        dragHandle === "control" ||
        (dragHandle === "middleV" || dragHandle === "middleH");

      if (shouldUpdate) {
        // Get the target wall
        const targetWall = activeStore?.wallsMap.get(edgeConnection.targetWallId) as WallType | undefined;
        if (targetWall) {
          // Recalculate the edge connection with updated positions
          const updatedConnection = updateEdgeConnectionForDrag(wall, targetWall, edgeConnection);
          if (updatedConnection) {
            // Wrap edge connection updates in runInAction for consistent MobX reactivity
            runInAction(() => {
              // Update the edge connection in the source wall using proper MobX pattern
              const connectionIndex = wall.edgeConnections?.findIndex(
                ec => ec.targetWallId === edgeConnection.targetWallId &&
                  ec.sourceEndpoint === edgeConnection.sourceEndpoint &&
                  ec.targetEndpoint === edgeConnection.targetEndpoint
              );
              if (connectionIndex !== undefined && connectionIndex >= 0 && wall.edgeConnections) {
                // Create new array to ensure MobX detects the change
                const newEdgeConnections = [...wall.edgeConnections];
                newEdgeConnections[connectionIndex] = updatedConnection;
                wall.edgeConnections = newEdgeConnections;
              }

              // Update the reciprocal edge connection in the target wall
              // Skip reciprocal update for middle-point connections (T-junctions)
              if (edgeConnection.targetEndpoint !== "middle") {
                const reciprocalIndex = targetWall.edgeConnections?.findIndex(
                  ec => ec.targetWallId === wall.id &&
                    ec.sourceEndpoint === edgeConnection.targetEndpoint &&
                    ec.targetEndpoint === edgeConnection.sourceEndpoint
                );

                // Create reciprocal connection (swap source and target)
                const reciprocalConnection: WallEdgeConnection = {
                  id: wall.id,
                  sourceWallId: targetWall.id,
                  targetWallId: wall.id,
                  sourceEndpoint: edgeConnection.targetEndpoint,
                  targetEndpoint: edgeConnection.sourceEndpoint,
                  sourceEdgePoint: updatedConnection.targetEdgePoint,
                  targetEdgePoint: updatedConnection.sourceEdgePoint,
                  gapDistance: updatedConnection.gapDistance,
                  connectionAngle: updatedConnection.connectionAngle,
                };

                if (reciprocalIndex !== undefined && reciprocalIndex >= 0 && targetWall.edgeConnections) {
                  // Update existing reciprocal connection
                  const newTargetEdgeConnections = [...targetWall.edgeConnections];
                  newTargetEdgeConnections[reciprocalIndex] = reciprocalConnection;
                  targetWall.edgeConnections = newTargetEdgeConnections;
                  activeStore?.incrementWallGeometryVersion(targetWall.id);
                } else if (targetWall.edgeConnections) {
                  // Fallback: reciprocal not found, append it
                  targetWall.edgeConnections = [...targetWall.edgeConnections, reciprocalConnection];
                  activeStore?.incrementWallGeometryVersion(targetWall.id);
                }
              }
            });

            // Mark target wall for update
            newUpdatedWalls.push(edgeConnection.targetWallId);

            // Mark target wall's joints as dirty
            const targetJoints = renderStore.getJointsForWall(edgeConnection.targetWallId) || [];
            targetJoints.forEach(joint => {
              jointCoordinator.markJointDirty(joint.id);
            });
          }
        }
      }
    });
  }

  // Update center-based connections (mid-point connections only), then cascade
  // through any objects that moved. The visited source set prevents cycles such
  // as A -> B -> C -> A from revisiting and moving earlier objects again.
  const changedByCenterConnections = new Set<string>();
  const cascadeQueue: string[] = [];
  const visitedCascadeSources = new Set<string>([source]);
  const applyCenterConnectionCascade = (
    sourceObject: ConnectableLineObject,
    sourceDragHandle: string | null,
  ) => {
    const changedIds = updateCenterConnectionsForSource(
      sourceObject,
      sourceDragHandle,
      activeStore,
      visitedCascadeSources,
    );

    changedIds.forEach((changedId) => {
      updated = true;
      changedByCenterConnections.add(changedId);
      if (!visitedCascadeSources.has(changedId)) {
        cascadeQueue.push(changedId);
      }
    });
  };

  if (isConnectableLineObject(wall)) {
    applyCenterConnectionCascade(wall, dragHandle);
  }

  if (dragHandle !== null && dragHandle !== "wall") {
    wall.centerConnections?.forEach((connection) => {
      const shouldCascadeFromDirectConnection =
        dragHandle === "middleV" ||
        dragHandle === "middleH" ||
        dragHandle === "control" ||
        (dragHandle === "start" && connection.sourcePosition === WallConnectionStart) ||
        (dragHandle === "end" && connection.sourcePosition === WallConnectionEnd);

      if (
        connection.mode !== "edge" &&
        shouldCascadeFromDirectConnection &&
        !visitedCascadeSources.has(connection.id)
      ) {
        cascadeQueue.push(connection.id);
      }
    });

    if (isWallType(wall)) {
      wall.edgeConnections?.forEach((connection) => {
        const shouldCascadeFromDirectConnection =
          dragHandle === "middleV" ||
          dragHandle === "middleH" ||
          dragHandle === "control" ||
          dragHandle === connection.sourceEndpoint;

        if (shouldCascadeFromDirectConnection && !visitedCascadeSources.has(connection.targetWallId)) {
          cascadeQueue.push(connection.targetWallId);
        }
      });
    }
  }

  while (cascadeQueue.length > 0) {
    const cascadeSourceId = cascadeQueue.shift()!;
    if (visitedCascadeSources.has(cascadeSourceId)) {
      continue;
    }
    visitedCascadeSources.add(cascadeSourceId);

    const cascadeSource = activeStore.findObjectId(cascadeSourceId);
    if (!isConnectableLineObject(cascadeSource)) {
      continue;
    }

    applyCenterConnectionCascade(cascadeSource, "cascade");
  }

  changedByCenterConnections.forEach((changedId) => {
    newUpdatedWalls.push(changedId);
  });
  if (isWallType(wall)) {
    wall.symbolAttachments?.forEach((symbolAttachment) => {
      // Update symbol attachment positions
      const symbol = activeStore?.symbolsMap.get(symbolAttachment.symbolId);
      if (!symbol) return;
      const tempWall = activeStore?.wallsMap.get(wall.id);
      if (!tempWall) return;

      const defaultFrameWidth = activeStore?.doorFrameWidth ?? 0.05;
      const insertGeom = getSymbolInsertGeometry(
        tempWall,
        symbol,
        symbolAttachment.positionFromStart,
        defaultFrameWidth,
      );
      const renderCenterOffset = getSymbolRenderCenterOffset(
        tempWall,
        symbol,
        symbolAttachment.positionFromStart,
        defaultFrameWidth,
      );
      const newPosition = insertGeom.midpoint
        .clone()
        .sub(insertGeom.tangent.clone().multiplyScalar(renderCenterOffset));
      const newWallAngle = Math.atan2(insertGeom.tangent.y, insertGeom.tangent.x);

      // Only update if the position or rotation actually changed
      const positionChanged = newPosition.x !== symbol.position.x || newPosition.y !== symbol.position.y;
      const rotationChanged = (symbol.rotation ?? 0) !== newWallAngle;
      if (positionChanged || rotationChanged) {
        activeStore?.updateSymbolProperty(
          symbolAttachment.symbolId,
          "position",
          newPosition.toArray(),
        );
        activeStore?.updateSymbolProperty(
          symbolAttachment.symbolId,
          "rotation",
          newWallAngle,
        );
        updateWallClippings(wall.id, symbol.id, activeStore);
      }
    });
  }
  // Check if any connection has been updated, and if so, use the cached update manager
  if ((updated || newUpdatedWalls.length > 0) && wallUpdateManager) {
    try {
      // Use the cached wall update manager for better performance
      wallUpdateManager.updateWallConnections(source);

      // Track every wall that needs a final recompute after all connection changes settle
      const wallsNeedingUpdate = new Set<string>(newUpdatedWalls);
      wallsNeedingUpdate.add(source);

      // Mark joints as dirty for the source wall and all connected walls
      // This ensures joint processing order is recalculated when connections change
      try {
        // Notify SharedJointStateManager about wall reconnection
        const isDragOperation = activeStore?.isDragInProgress ? activeStore.isDragInProgress() : false;

        if (isWallType(wall)) {
          const wallPosition = wall.start.clone().add(wall.end).divideScalar(2); // Rough center position
          sharedJointStateManager.notifyWallReconnected(source, wallPosition, isDragOperation);
        }

        // Get joints for the source wall from renderStore
        const sourceJoints = renderStore.getJointsForWall(source) || [];
        sourceJoints.forEach(joint => {
          jointCoordinator.markJointDirty(joint.id);
        });

        // Also mark joints dirty for all connected walls
        wall.centerConnections?.forEach((c) => {
          const connectedJoints = renderStore.getJointsForWall(c.id) || [];
          connectedJoints.forEach(joint => {
            jointCoordinator.markJointDirty(joint.id);
          });
          wallsNeedingUpdate.add(c.id);
        });
      } catch (jointError) {
        console.error('Error marking joints as dirty:', jointError);
        // Don't crash the connection update if joint marking fails
      }

      // Include the previously connected walls captured before the drag finished
      newUpdatedWalls.forEach(id => wallsNeedingUpdate.add(id));

      // Refresh symbol attachments and clipping polygons for any wall whose geometry moved.
      // This keeps door/window/void holes aligned when connected walls update.
      wallsNeedingUpdate.forEach(wallId => {
        updateSymbolAttachmentsDuringDrag(wallId, activeStore);
      });

      // CRITICAL: Increment geometry versions for all affected walls
      // This ensures cached geometry is invalidated and walls rebuild correctly
      activeStore.incrementWallGeometryVersion(source);
      wallsNeedingUpdate.forEach(wallId => {
        activeStore.incrementWallGeometryVersion(wallId);
      });

      // Update all affected walls in a single batch operation
      wallsNeedingUpdate.forEach(wallId => {
        if (wallId === source) {
          return;
        }
        wallUpdateManager.updateWallConnections(wallId);
      });

      const wallsToInvalidate = new Set<string>([source]);
      wallsNeedingUpdate.forEach(id => wallsToInvalidate.add(id));

      if (typeof wallUpdateManager.processPendingUpdatesNow === 'function') {
        const flushPromise = wallUpdateManager.processPendingUpdatesNow();
        if (flushPromise && typeof (flushPromise as Promise<void>).then === 'function') {
          (flushPromise as Promise<void>)
            .then(() => {
              wallsToInvalidate.forEach(wallId => {
                renderStore.requestInvalidation(wallId);
              });
            })
            .catch(error => {
              console.error('Error flushing pending wall updates:', error);
            });
        } else {
          wallsToInvalidate.forEach(wallId => {
            renderStore.requestInvalidation(wallId);
          });
        }
      } else {
        wallsToInvalidate.forEach(wallId => {
          renderStore.requestInvalidation(wallId);
        });
      }
    } catch (error) {
      console.error('Error updating wall connections:', error);
      // Don't crash the drag operation if connection updates fail
    }
  }
};

/**
 * Recalculates an edge connection after wall position changes
 * @param sourceWall - The source wall that was moved
 * @param targetWall - The target wall being connected to
 * @param oldConnection - The previous edge connection
 * @returns Updated WallEdgeConnection object
 */
const updateEdgeConnectionForDrag = (
  sourceWall: WallType,
  targetWall: WallType,
  oldConnection: WallEdgeConnection
): WallEdgeConnection | null => {
  try {
    return recomputeEdgeConnectionForCurrentGeometry(sourceWall, targetWall, oldConnection);
  } catch (error) {
    console.error('Error updating edge connection for drag:', error);
    return null;
  }
};
